import React, { useEffect, useRef } from "react";
import Placeholder from "../../../images/Place Holder 3 1.webp";

// ────────────────────────────────────────────────
// Better parser – works for both message & reply_to
// ────────────────────────────────────────────────
const parseInteractivePayload = (raw) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed?.type !== "interactive" || parsed?.interactive?.type !== "button") {
      return null;
    }
    return {
      body: parsed.interactive.body?.text || "",
      buttons: parsed.interactive.action?.buttons || [],
    };
  } catch {
    return null;
  }
};

// Keep the old name for compatibility with buildRenderList
const parseInteractiveMessage = (chat) => {
  if (chat.message_type !== "text" || !chat.message) return null;
  return parseInteractivePayload(chat.message);
};

// ────────────────────────────────────────────────
// Nice quote bubble for reply_to
// ────────────────────────────────────────────────
const renderReplyQuote = (replyTo) => {
  if (!replyTo) return null;

  // Case 1: Interactive WhatsApp message (JSON)
  const interactive = parseInteractivePayload(replyTo);
  if (interactive) {
    return (
      <div className="border border-[#ccc] border-l-[3px] border-l-[#01C37D] rounded text-[13px] text-left bg-white text-[#555] mb-[5px] px-[10px] py-[6px]">
        <div className="whitespace-pre-wrap break-words leading-[1.4] text-[#111b21]">
          {renderMarkdown(interactive.body)}
        </div>
        {interactive.buttons?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {interactive.buttons.map((btn, i) => (
              <span
                key={btn.reply?.id || i}
                className="inline-block bg-[#f0f2f5] text-[#00a884] text-[12px] font-medium px-2 py-0.5 rounded"
              >
                {btn.reply?.title || "Option"}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Case 2: Plain text (or already cleaned string)
  const text = typeof replyTo === "string" ? replyTo : JSON.stringify(replyTo);
  // Avoid dumping huge JSON if it somehow slips through
  if (text.startsWith("{") && text.includes("messaging_product")) {
    return (
      <div className="border border-[#ccc] border-l-[3px] border-l-[#01C37D] rounded text-[13px] text-left bg-white text-[#555] mb-[5px] px-[10px] py-[6px]">
        Interactive message
      </div>
    );
  }

  return (
    <div className="border border-[#ccc] border-l-[3px] border-l-[#01C37D] rounded text-[13px] text-left bg-white text-[#555] mb-[5px] px-[10px] py-[6px] whitespace-pre-wrap break-words">
      {text}
    </div>
  );
};

const buildRenderList = (chats = []) => {
  const ordered = [...chats].reverse();
  const result = [];
  let i = 0;

  const PREFERENCE_KEYWORDS = [
    "please select your prefrence",
    "please select your preference",
  ];

  const isPreferenceText = (chat) =>
    chat.message_type === "text" &&
    PREFERENCE_KEYWORDS.includes(chat.message?.trim().toLowerCase());

  while (i < ordered.length) {
    const current = ordered[i];

    // Interactive WhatsApp message stored as JSON in a text message
    const interactive = parseInteractiveMessage(current);

    if (interactive) {
      result.push({
        kind: "interactive",
        chat: current,
        body: interactive.body,
        buttons: interactive.buttons,
      });

      i++;

      // Skip duplicate button records belonging to this interactive message
      while (
        i < ordered.length &&
        ordered[i].message_type === "button" &&
        ordered[i].sender === current.sender
      ) {
        i++;
      }
    }

    // Preference message followed by its button responses
    else if (
      isPreferenceText(current) &&
      i + 1 < ordered.length &&
      ordered[i + 1]?.message_type === "button"
    ) {
      const headerText = current.message;
      const group = [];
      i++;

      while (i < ordered.length && ordered[i].message_type === "button") {
        group.push(ordered[i]);
        i++;
      }

      result.push({
        kind: "button_group",
        buttons: group,
        headerText,
      });
    }

    // Standalone button or consecutive button group
    else if (current.message_type === "button") {
      const group = [];

      while (i < ordered.length && ordered[i].message_type === "button") {
        group.push(ordered[i]);
        i++;
      }

      if (group.length === 1) {
        result.push({
          kind: "single_button",
          chat: group[0],
        });
      } else {
        result.push({
          kind: "button_group",
          buttons: group,
          headerText: "Please Select Your Preference",
        });
      }
    }

    // All other message types
    else {
      result.push({
        kind: "single",
        chat: current,
      });
      i++;
    }
  }

  return result;
};

const renderMarkdown = (text) => {
  if (!text) return null;

  return text.split("\n").map((line, li, arr) => {
    // Support both **bold** and *bold* (WhatsApp style)
    const parts = line.split(/(\*\*.*?\*\*|\*[^*\n]+?\*)/g);

    return (
      <span key={li}>
        {parts.map((part, pi) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={pi}>{part.slice(2, -2)}</strong>;
          }
          if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
            return <strong key={pi}>{part.slice(1, -1)}</strong>;
          }
          return <span key={pi}>{part}</span>;
        })}
        {li < arr.length - 1 && <br />}
      </span>
    );
  });
};

const ChatWindow = ({ chats, isLoadingChats, chatHasMore, fetchMoreChats }) => {
  const chatContainerRef = useRef(null);
  const prevHeight = useRef(0);
  const bottomRef = useRef(null);
  const loadingOld = useRef(false);

  const handleScroll = (e) => {
    const container = e.currentTarget;
    if (container.scrollTop < 120 && chatHasMore && !isLoadingChats) {
      prevHeight.current = container.scrollHeight;
      loadingOld.current = true;
      fetchMoreChats();
    }
  };

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (loadingOld.current) {
      container.scrollTop = container.scrollHeight - prevHeight.current;
      loadingOld.current = false;
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [chats]);

  const renderMessage = (chat) => {
    /* TEXT MESSAGE */
    if (chat.message_type === "text") {
      return (
        <p className="whitespace-pre-line text-left p-0 max-w-[500px] break-words m-0">
          {renderMarkdown(chat.message)}
        </p>
      );
    }

    /* IMAGE MESSAGE */
    if (chat.message_type === "image") {
      return (
        <div className="bg-[#E7FDD8] p-3 rounded-lg inline-block text-center">
          <img
            src={chat.message}
            alt="chat"
            className="w-full max-w-[350px] min-w-[250px] object-contain rounded bg-white block mx-auto"
            onError={(e) => {
              e.currentTarget.src = Placeholder;
            }}
          />
        </div>
      );
    }

    /* CATALOGUE MESSAGE */
if (chat.message_type === "catalogue") {
  return (
    <div className="rounded-[10px] overflow-hidden border border-[#ddd] bg-[#D9FDD3] text-left p-[10px]">
      {chat.reply_to && renderReplyQuote(chat.reply_to)}

          <div className="flex">
            <img
              src="https://images.unsplash.com/photo-1550547660-d9450f859349"
              className="w-[150px] h-20 object-cover shrink-0"
              alt="catalogue"
              onError={(e) => {
                e.currentTarget.src = Placeholder;
              }}
            />
            <div className="flex items-center p-[10px] text-left bg-white text-black text-[13px]">
              <b>Your Catalogue on WhatsApp</b>
            </div>
          </div>

          <div className="p-[10px] text-[13px] text-left leading-[1.6] break-all whitespace-pre-wrap text-[#111] bg-[#D9FDD3]">
            {chat.message}
          </div>

          <div className="border-t border-[#eee]" />

          <div
            className="p-[10px] text-center text-[#00a884] font-semibold cursor-pointer bg-white text-[14px]"
            onClick={() => window.open(chat.message)}
          >
            View Catalogue
          </div>

          <div className="mt-[5px] text-right text-[14px] text-[#667781]">
            {chat.datetime}
          </div>
        </div>
      );
    }

    /* DOCUMENT MESSAGE */
    if (chat.message_type === "document") {
      return (
        <a href={chat.message} target="_blank" rel="noreferrer">
          📄 View Document
        </a>
      );
    }

    /* VIDEO MESSAGE */
    if (chat.message_type === "video") {
      return (
        <video controls src={chat.message} className="w-full rounded-lg" />
      );
    }

    /* TEMPLATE MESSAGE */
    if (chat.message_type === "template") {
      const header = chat.message?.components?.find((c) => c.type === "HEADER");
      const body = chat.message?.components?.find((c) => c.type === "BODY");
      const footer = chat.message?.components?.find((c) => c.type === "FOOTER");

      const headerImageUrl =
        header?.format === "IMAGE" && header?.example?.header_handle?.[0]
          ? header.example.header_handle[0]
          : header?.format === "IMAGE" && header?.parameters?.[0]?.image?.link
            ? header.parameters[0].image.link
            : null;

      return (
        <div className="max-w-[300px] p-[10px]">
          {header?.format === "IMAGE" && (
            <img
              src={headerImageUrl || Placeholder}
              alt="template"
              className="w-full h-[300px] object-cover rounded-md mb-3"
              onError={(e) => {
                e.currentTarget.src = Placeholder;
              }}
            />
          )}

          {body?.text && (
            <div className="text-[14px] whitespace-pre-wrap leading-[1.6] text-left">
              {renderMarkdown(body.text)}
            </div>
          )}

          {footer?.text && (
            <div className="text-[16px] mt-1.5 text-[#667781] text-left">
              {footer.text}
            </div>
          )}
        </div>
      );
    }
    return <p className="m-0">{chat.message}</p>;
  };

  // Single button (e.g. "Pay Now")
  const renderSingleButton = (chat) => {
    const isSent = chat.sender === "1";
    return (
      <div
        key={chat.message_id}
        className={`mb-[10px] ${isSent ? "text-right" : "text-left"}`}
      >
        <div
          className={`inline-block w-full max-w-[350px] min-w-[250px] rounded-[10px] overflow-hidden border border-[#e0e0e0] shadow-[0_1px_2px_rgba(0,0,0,0.08)] ${
            isSent ? "bg-white" : "bg-[#d9fdd3]"
          }`}
        >
          <div className="px-4 py-[11px] text-[14px] font-semibold text-[#00a884] text-center cursor-pointer">
            {chat.message}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Interactive message (WhatsApp button reply style)
   * Matches the screenshot exactly:
   * - Green bubble with body text + timestamp
   * - Separate white rounded buttons stacked below
   */
const renderInteractiveMessage = (item, idx) => {
  const chat = item.chat;

  return (
    <div
      key={chat.message_id || `interactive-${idx}`}
      className="mb-[10px] text-right"
    >
      {/* Main green bubble – matches original WhatsApp */}
      <div className="inline-block w-full max-w-[320px] overflow-hidden rounded-[7.5px] bg-[#D9FDD3] text-left shadow-[0_1px_0.5px_rgba(0,0,0,0.13)]">
        <div className="px-[9px] pt-[6px] pb-[4px]">
          <p className="m-0 whitespace-pre-wrap break-words text-[14.2px] leading-[19px] text-[#111b21]">
            {renderMarkdown(item.body)}
          </p>
        </div>

        {/* Timestamp – bottom right, exact WhatsApp style */}
        <div className="flex justify-end items-center px-[9px] pb-[5px] pt-0">
          <span className="text-[11px] leading-[15px] text-[#667781]">
            {chat.datetime}
          </span>
        </div>
      </div>

      {/* Separate white buttons underneath */}
      {item.buttons.length > 0 && (
        <div className="mt-[6px] flex flex-col gap-[6px] max-w-[320px] ml-auto">
          {item.buttons.map((button, buttonIndex) => (
            <div
              key={button.reply?.id || buttonIndex}
              className="bg-white rounded-[7.5px] border border-[#e9e9e9] px-4 py-[11px] text-center text-[14.5px] font-medium text-[#00a884] cursor-pointer shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] hover:bg-[#f5f5f5] transition-colors"
            >
              {button.reply?.title || "Option"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

  /**
   * Button group (preference style)
   * Also rendered as separate white buttons under a green header bubble
   */
  const renderButtonGroup = (buttons, headerText, idx) => {
    const isSent = buttons[0]?.sender === "1";

    return (
      <div
        key={`btn_group_${idx}`}
        className={`mb-[10px] ${isSent ? "text-right" : "text-left"}`}
      >
        {/* Header bubble */}
        <div
          className={`inline-block w-full max-w-[320px] overflow-hidden rounded-[8px] ${
            isSent ? "bg-[#D9FDD3]" : "bg-[#E7FDD8]"
          } text-left shadow-sm`}
        >
          <div className="px-[12px] pt-[10px] pb-[2px] text-[14px] text-[#111b21]">
            {headerText}
          </div>
          <div className="px-[10px] pb-[6px] pt-[2px] text-right text-[11px] text-[#667781]">
            {buttons[0]?.datetime}
          </div>
        </div>

        {/* Separate white buttons */}
        <div
          className={`mt-[6px] flex flex-col gap-[6px] max-w-[320px] ${
            isSent ? "ml-auto" : ""
          }`}
        >
          {buttons.map((btn, bIdx) => (
            <div
              key={btn.message_id || bIdx}
              className="bg-white rounded-[8px] border border-[#e0e0e0] px-4 py-[11px] text-center text-[14.5px] font-medium text-[#00a884] cursor-pointer shadow-[0_1px_1px_rgba(0,0,0,0.05)] hover:bg-[#f8f8f8] transition-colors"
            >
              {btn.message}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderList = buildRenderList(chats || []);

  return (
    <div
      ref={chatContainerRef}
      onScroll={handleScroll}
      className="hide-scrollbar h-full overflow-y-auto p-5"
    >
      {isLoadingChats && <div className="text-center py-2">Loading...</div>}

      {renderList.map((item, idx) => {
        if (item.kind === "button_group") {
          return renderButtonGroup(item.buttons, item.headerText, idx);
        }

        if (item.kind === "single_button") {
          return renderSingleButton(item.chat);
        }

        if (item.kind === "interactive") {
          return renderInteractiveMessage(item, idx);
        }

        /* ── NORMAL SINGLE MESSAGE ── */
        const chat = item.chat;
        const hideTimestamp = [
          "image",
          "video",
          "document",
          "template",
          "catalogue",
        ].includes(chat.message_type);

        return (
          <div
            key={chat.message_id}
            className={`mb-[10px] ${
              chat.sender === "1" ? "text-right" : "text-left"
            }`}
          >
<div
  className={`inline-block max-w-[320px] rounded-[7.5px] shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] ${
    ["image", "video", "catalogue"].includes(chat.message_type)
      ? "p-0"
      : "px-[9px] pt-[6px] pb-[4px]"
  } ${chat.sender === "1" ? "bg-[#D9FDD3]" : "bg-[#ffffff]"}`}
>
  {/* reply_to ... */}
{chat.reply_to && chat.message_type !== "catalogue" && (
  renderReplyQuote(chat.reply_to)
)}
  {renderMessage(chat)}

  {!hideTimestamp && (
    <div className="flex justify-end items-center pt-[2px] pb-[1px]">
      <span className="text-[11px] leading-[15px] text-[#667781]">
        {chat.datetime}
      </span>
    </div>
  )}
</div>
          </div>
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
};

export default ChatWindow;